/**
 * Diagnostic — moteur de recommandation.
 *
 * Transcription de la spec D:\DL\logique-diagnostic-kqueo.md (§ 7, 9 et 10).
 * Aucun accès au DOM : c'est du calcul pur, appelé par diagnostic.js.
 *
 * Deux couches, volontairement séparées (§ 10) :
 *   - `select()` ne raisonne que besoin / budget, et ignore le stock. Il peut
 *     donc désigner un produit non vendable — c'est voulu.
 *   - `resolve()` est la seule couche qui regarde la disponibilité.
 *
 * Les prix ci-dessous sont ceux de la spec (§ 0) : ils servent uniquement à
 * arbitrer (comparaison aux paliers de budget, ordre des dégradations).
 * `applyLive()` les rafraîchit avec les données réelles de la boutique avant
 * la résolution, et l'affichage se fait de toute façon sur le produit live.
 */

(() => {
  /**
   * `slug` alimente les tags client et le paramètre d'URL de la page de reco.
   * Il est explicite plutôt que dérivé du handle : les handles sont longs et
   * changent au gré du SEO, alors qu'un tag doit rester stable.
   */
  const CATALOG = {
    LIFT: { price: 385, sellable: true, family: 'desk', slug: 'lift', handle: 'lift-bureau-assis-debout' },
    DYNAMIC: { price: 449, sellable: true, family: 'desk', slug: 'dynamic', handle: 'dynamic-bureau-assis-debout' },
    BAMBOU: { price: 516, sellable: true, family: 'desk', slug: 'bambou', handle: 'dynamic-bambou-bureau-assis-debout-bambou' },
    CONNECT: { price: 529, sellable: true, family: 'desk', slug: 'connect', handle: 'bureau-connecte-assis-debout-connect' },
    CURVE: { price: 549, sellable: true, family: 'desk', slug: 'curve', handle: 'dynamic-curve-bureau-assis-debout-incurve' },
    SPACE: { price: 555, sellable: true, family: 'desk', slug: 'space', handle: 'space-dynamic-bureau-assis-debout-avec-rangement' },
    SMOOTH: { price: 569, sellable: true, family: 'desk', slug: 'smooth', handle: 'smooth-dynamic-bureau-assis-debout-incurve' },
    CORNER: { price: 599, sellable: false, family: 'desk', slug: 'corner', handle: 'corner-bureau-assis-debout-en-angle' },
    EXECUTIVE: { price: 695, sellable: true, family: 'desk', slug: 'executive', handle: 'dynamic-executive-bureau-assis-debout-avec-plateau-en-verre' },

    LAMA: { price: 200, sellable: false, family: 'chair', slug: 'lama', handle: 'lama-chaise-ergonomique-de-bureau' },
    ELENA: { price: 280, sellable: true, family: 'chair', slug: 'elena', handle: 'chaise-ergonomique-bureau-elena' },
    EPSILON: { price: 345, sellable: true, family: 'chair', slug: 'epsilon', handle: 'epsilon-chaise-ergonomique-de-bureau' },
    TERRANA: { price: 389, sellable: true, family: 'chair', slug: 'terrana', handle: 'terrana-chaise-ergonomique-de-bureau' },
    TERRANA_B: { price: 399, sellable: true, family: 'chair', slug: 'terrana-bouclette', handle: 'chaise-ergonomique-tissu-bouclette-terrana' },
    WAVE: { price: 399, sellable: false, family: 'chair', slug: 'wave', handle: 'wave-chaise-ergonomique-de-bureau' },
    CLARA: { price: 415, sellable: true, family: 'chair', slug: 'clara', handle: 'chaise-ergonomique-bureau-clara' },
    TERRANA_M: { price: 419, sellable: true, family: 'chair', slug: 'terrana-maille', handle: 'chaise-ergonomique-de-bureau-terrana-en-maille' },
    TERRANA_P: { price: 515, sellable: true, family: 'chair', slug: 'terrana-premium', handle: 'chaise-ergonomique-bureau-terrana-premium' },
    WAVE_P: { price: 515, sellable: false, family: 'chair', slug: 'wave-premium', handle: 'chaise-ergonomique-bureau-wave-premium' },
    TERRANA_PC: { price: 529, sellable: true, family: 'chair', slug: 'terrana-premium-cuir', handle: 'chaise-ergonomique-bureau-terrana-premium-noir-cuir-reconstitue' },
    AURA: { price: 535, sellable: true, family: 'chair', slug: 'aura', handle: 'chaise-ergonomique-gamer-aura-premium-noir-mousse-maille' },
    VERTEX: { price: 545, sellable: true, family: 'chair', slug: 'vertex', handle: 'chaise-ergonomique-bureau-vertex' },
    VERTEX_C: { price: 545, sellable: true, family: 'chair', slug: 'vertex-cuir', handle: 'chaise-ergonomique-de-bureau-vertex-en-cuir-reconstitue' },
    NOVA: { price: 629, sellable: true, family: 'chair', slug: 'nova', handle: 'siege-ergonomique-haut-de-gamme-nova' },
    FLOW: { price: 649, sellable: true, family: 'chair', slug: 'flow', handle: 'chaise-active-dynamique-flow' },

    SPHERA_STD: { price: 89, sellable: false, family: 'seat', slug: 'sphera', handle: 'ballon-ergonomique-sphera' },
    SPHERA_B: { price: 99, sellable: true, family: 'seat', slug: 'sphera-bouclette', handle: 'ballon-ergonomique-sphera-tissu-bouclette' },
    WOOBLE: { price: 144, sellable: true, family: 'seat', slug: 'wooble', handle: 'wooble-tabouret-assis-debout' },
    MOOVE: { price: 164, sellable: true, family: 'seat', slug: 'moove', handle: 'moove-chaise-de-bureau-ergonomique' },
    MOOVE_V: { price: 164, sellable: false, family: 'seat', slug: 'moove-vintage', handle: 'chaise-assis-genoux-moove-vintage-similicuir' },
    MOOVE_B: { price: 174, sellable: true, family: 'seat', slug: 'moove-bouclette', handle: 'chaise-assis-genoux-moove-tissu-bouclette' },
    ZEN: { price: 179, sellable: true, family: 'seat', slug: 'zen', handle: 'chaise-ergonomique-assis-en-tailleur-zen' },
    ZEN_B: { price: 189, sellable: true, family: 'seat', slug: 'zen-bouclette', handle: 'chaise-assis-en-tailleur-zen-tissu-bouclette' }
  };

  const FALLBACK = {
    CONNECT: ['EXECUTIVE', 'SMOOTH', 'DYNAMIC'],
    SMOOTH: ['CURVE', 'SPACE', 'DYNAMIC'],
    CURVE: ['SMOOTH', 'SPACE', 'DYNAMIC'],
    SPACE: ['DYNAMIC', 'LIFT'],
    BAMBOU: ['DYNAMIC', 'SPACE', 'LIFT'],
    EXECUTIVE: ['CONNECT', 'SMOOTH', 'DYNAMIC'],
    DYNAMIC: ['LIFT', 'SPACE'],
    LIFT: ['DYNAMIC'],
    CORNER: ['SPACE', 'SMOOTH', 'DYNAMIC'],
    LAMA: ['ELENA', 'EPSILON', 'TERRANA'],
    ELENA: ['EPSILON', 'LAMA', 'TERRANA'],
    EPSILON: ['ELENA', 'TERRANA'],
    TERRANA: ['WAVE', 'TERRANA_M', 'TERRANA_B'],
    TERRANA_B: ['TERRANA', 'TERRANA_M'],
    TERRANA_M: ['TERRANA', 'CLARA', 'TERRANA_B'],
    TERRANA_P: ['WAVE_P', 'AURA', 'VERTEX', 'TERRANA_M'],
    VERTEX: ['AURA', 'TERRANA_P', 'NOVA', 'TERRANA_M'],
    VERTEX_C: ['TERRANA_PC', 'VERTEX', 'AURA'],
    TERRANA_PC: ['VERTEX_C', 'VERTEX', 'TERRANA_P'],
    FLOW: ['NOVA', 'VERTEX', 'TERRANA_P'],
    NOVA: ['FLOW', 'VERTEX', 'AURA'],
    CLARA: ['TERRANA', 'TERRANA_M'],
    AURA: ['TERRANA', 'TERRANA_M'],
    WAVE: ['TERRANA', 'TERRANA_M'],
    WAVE_P: ['TERRANA_P', 'VERTEX'],
    SPHERA_STD: ['SPHERA_B', 'WOOBLE'],
    SPHERA_B: ['SPHERA_STD', 'WOOBLE'],
    WOOBLE: ['SPHERA_B', 'MOOVE'],
    MOOVE: ['MOOVE_B', 'MOOVE_V', 'ZEN'],
    MOOVE_B: ['MOOVE', 'MOOVE_V', 'ZEN_B'],
    MOOVE_V: ['MOOVE', 'MOOVE_B'],
    ZEN: ['ZEN_B', 'MOOVE'],
    ZEN_B: ['ZEN', 'MOOVE_B']
  };

  const FAMILY_SAFETY = {
    desk: ['DYNAMIC', 'LIFT'],
    chair: ['TERRANA', 'TERRANA_B'],
    seat: ['ZEN', 'ZEN_B']
  };

  /** Chaînes de dégradation, triées par prix décroissant (§ 7). */
  const CHAINS = {
    desk: ['EXECUTIVE', 'SMOOTH', 'SPACE', 'CURVE', 'CONNECT', 'BAMBOU', 'DYNAMIC', 'LIFT'],
    chair: [
      'FLOW', 'NOVA', 'VERTEX_C', 'VERTEX', 'AURA', 'TERRANA_PC', 'TERRANA_P',
      'TERRANA_M', 'CLARA', 'TERRANA_B', 'TERRANA', 'EPSILON', 'ELENA', 'LAMA'
    ],
    seat: ['ZEN_B', 'ZEN', 'MOOVE_B', 'MOOVE', 'WOOBLE', 'SPHERA_B', 'SPHERA_STD']
  };

  const price = (key) => CATALOG[key].price;
  const family = (key) => CATALOG[key].family;
  const sellable = (key) => CATALOG[key].sellable;

  /** @returns {number} Plafond numérique ; `MAX` n'a pas de plafond. */
  const ceilOf = (budget) => (budget === 'MAX' ? Infinity : Number(budget));

  /** Élément suivant (moins cher) de la chaîne. */
  const nextIn = (chain, key) => {
    const list = CHAINS[chain];
    const i = list.indexOf(key);
    return i === -1 || i === list.length - 1 ? key : list[i + 1];
  };

  /** Produit le plus cher de la chaîne dont le prix tient dans le plafond. */
  const clamp = (chain, key, ceil) => {
    if (price(key) <= ceil) return key;
    const found = CHAINS[chain].find((k) => price(k) <= ceil);
    return found || CHAINS[chain][CHAINS[chain].length - 1];
  };

  /**
   * Choix commercial pur — ignore volontairement le stock (§ 10).
   * @param {Object<string, string>} a - Réponses, par clé de question.
   * @returns {{main: string[], cross: string[]}}
   */
  function select(a) {
    const ceil = ceilOf(a.budget);

    if (a.need === 'bureau') {
      let d =
        a.prio === 'ecolo' ? 'BAMBOU'
        : a.prio === 'rangement' ? 'SPACE'
        : a.prio === 'design'
          ? (a.budget === 'MAX' ? 'CONNECT' : ceil >= 569 ? 'SMOOTH' : ceil >= 549 ? 'CURVE' : 'DYNAMIC')
        : ceil <= 400 ? 'LIFT'
        : ceil <= 550 ? 'DYNAMIC'
        : ceil <= 700 ? 'SMOOTH'
        : 'CONNECT';

      // Garde-fou espace : ne s'applique pas aux priorités rangement /
      // design / ecolo, tous ces modèles existant en 120 cm (§ 3).
      if (a.space === 'petit' && a.prio === 'qp' && price(d) > 400) d = 'LIFT';

      d = clamp('desk', d, ceil);
      return { main: [d], cross: [ceil >= 550 ? 'TERRANA' : 'ELENA'] };
    }

    if (a.need === 'chaise') {
      const hard = a.prio === 'maintien' || a.time === 'high';

      let c =
        a.matiere === 'bouclette' || a.prio === 'cosy' ? 'TERRANA_B'
        : a.prio === 'respirabilite' || (a.matiere === 'maille' && a.prio !== 'maintien') ? 'TERRANA_M'
        : a.time === 'high' ? (ceil >= 545 ? 'VERTEX' : 'TERRANA')
        : a.prio === 'maintien' ? (ceil >= 515 ? 'TERRANA_P' : 'TERRANA')
        : ceil <= 350 ? 'LAMA'
        : 'TERRANA';

      if (a.budget === 'MAX' && hard) c = 'FLOW';
      // La demande explicite de matière l'emporte sur FLOW (§ 4).
      if (a.matiere === 'cuir') c = ceil >= 545 ? 'VERTEX_C' : 'TERRANA';

      c = clamp('chair', c, ceil);
      return { main: [c], cross: [ceil >= 400 ? 'DYNAMIC' : 'LIFT'] };
    }

    if (a.need === 'duo') {
      const hard = a.prio === 'maintien' || a.time === 'high';

      let d =
        a.prio === 'rangement' ? 'SPACE'
        : a.prio === 'design' ? (a.budget === 'MAX' ? 'CONNECT' : 'CURVE')
        : ceil <= 750 ? 'LIFT'
        : ceil <= 950 ? 'DYNAMIC'
        : ceil <= 1200 ? 'SPACE'
        : 'SMOOTH';

      let c =
        a.style === 'bouclette' ? 'TERRANA_B'
        : hard ? (ceil >= 1200 ? 'VERTEX' : 'TERRANA_P')
        : ceil <= 750 ? 'ELENA'
        : ceil <= 950 ? 'TERRANA'
        : 'TERRANA_B';

      // 14 itérations et non 5 : le cas 750 € + rangement + bouclette demande
      // 9 descentes pour converger (§ 5).
      for (let i = 0; i < 14 && price(d) + price(c) > ceil; i += 1) {
        const dEnd = d === 'LIFT';
        const cEnd = c === 'LAMA';
        if (dEnd && cEnd) break;
        if (cEnd || (!dEnd && price(d) >= price(c))) d = nextIn('desk', d);
        else c = nextIn('chair', c);
      }

      return { main: [d, c], cross: [] };
    }

    // Parcours assise alternative (§ 6).
    const cosy = a.style === 'bouclette' || a.lieu === 'salon';

    let s =
      a.objectif === 'tailleur' ? (cosy ? 'ZEN_B' : 'ZEN')
      : a.objectif === 'dos' ? (cosy ? 'MOOVE_B' : 'MOOVE')
      : a.objectif === 'gainage' ? (cosy ? 'SPHERA_B' : 'SPHERA_STD')
      : 'WOOBLE';

    if (price(s) > ceil) {
      s = s === 'ZEN_B' ? 'ZEN' : s === 'MOOVE_B' ? 'MOOVE' : s === 'SPHERA_B' ? 'SPHERA_STD' : s;
      if (price(s) > ceil) s = 'SPHERA_STD';
    }

    const alt =
      s.indexOf('SPHERA') === 0 ? 'WOOBLE'
      : s === 'WOOBLE' ? 'MOOVE'
      : s.indexOf('MOOVE') === 0 ? 'ZEN'
      : 'MOOVE';

    return { main: [s], cross: [alt, a.lieu === 'debout' ? 'DYNAMIC' : 'LIFT'] };
  }

  /**
   * Seule couche qui regarde la disponibilité (§ 7).
   * Le badge « réassort » de la spec n'est pas repris : `sellable` suffit.
   * @param {string} key - Produit retenu par `select()`.
   * @param {number} ceil - Plafond de budget.
   * @returns {string} Le produit réellement affichable.
   */
  function resolve(key, ceil) {
    const safety = FAMILY_SAFETY[family(key)];
    const candidates = [key, ...(FALLBACK[key] || []), ...safety];

    // 1 · le produit choisi tient : on le garde, même s'il est cher.
    if (sellable(key) && price(key) <= ceil) return key;

    // 2 · sinon, premier repli vendable dans le budget.
    const inBudget = candidates.find((c) => sellable(c) && price(c) <= ceil);
    if (inBudget) return inBudget;

    // 3 · budget relâché plutôt que rien : le moins cher des vendables.
    const anySellable = candidates.filter(sellable);
    if (anySellable.length) {
      return anySellable.reduce((best, c) => (price(c) < price(best) ? c : best));
    }

    return safety[0];
  }

  /** Handles à rafraîchir pour une réponse donnée : le choix et ses replis. */
  function candidateKeys(answers) {
    const chosen = select(answers);
    const keys = new Set();

    chosen.main.forEach((key) => {
      keys.add(key);
      (FALLBACK[key] || []).forEach((k) => keys.add(k));
      FAMILY_SAFETY[family(key)].forEach((k) => keys.add(k));
    });

    return [...keys];
  }

  /**
   * Remplace prix et disponibilité par les données réelles de la boutique.
   * @param {string} key - Clé catalogue.
   * @param {object} product - Réponse de /products/{handle}.js
   */
  function applyLive(key, product) {
    if (!CATALOG[key] || !product || !Array.isArray(product.variants)) return;

    const available = product.variants.filter((v) => v.available);
    CATALOG[key].sellable = available.length > 0;

    // Prix mini des variantes *disponibles* : `price_min` pointe parfois vers
    // une variante en rupture (§ 0 — LIFT annoncé 330 €, 385 € en pratique).
    if (available.length) {
      CATALOG[key].price = Math.min(...available.map((v) => v.price)) / 100;
    }
  }

  /**
   * Recommandation finale : produits résolus, plus le tag / paramètre d'URL
   * qui les nomme.
   * @param {Object<string, string>} answers - Réponses, par clé de question.
   * @returns {{keys: string[], slug: string, products: object[]}}
   */
  function recommend(answers) {
    const ceil = ceilOf(answers.budget);
    const chosen = select(answers);
    let keys;

    if (answers.need === 'duo') {
      const [d, c] = chosen.main;
      // Budget restant pièce par pièce, sinon un repli plus cher fait sauter
      // l'enveloppe du couple (§ 5).
      keys = [resolve(d, ceil - price(c)), resolve(c, ceil - price(d))];
    } else {
      keys = chosen.main.map((key) => resolve(key, ceil));
    }

    const slugs = keys.map((key) => CATALOG[key].slug);
    const prefix = answers.need === 'assise' ? 'alternative' : answers.need;

    return {
      keys,
      cross: chosen.cross,
      slug: `diagnostic-${prefix}-${slugs.join('-')}`,
      products: keys.map((key) => ({ key, ...CATALOG[key] }))
    };
  }

  window.DiagnosticEngine = {
    CATALOG,
    FALLBACK,
    FAMILY_SAFETY,
    CHAINS,
    select,
    resolve,
    recommend,
    candidateKeys,
    applyLive
  };
})();
